import {
  Globe,
  User,
  Building2,
  ListTodo,
  DollarSign,
  Sparkles,
  MapPin,
  Network,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface ScopeOption {
  scope: string
  entityType?: string
  entityId?: string
  entityName?: string
  label: string
  description: string
  icon: string
  isCurrentPage?: boolean
}

interface Props {
  options: ScopeOption[]
  selectedScope: string
  onSelect: (option: ScopeOption) => void
  onClose: () => void
}

const ICON_MAP: Record<string, LucideIcon> = {
  globe: Globe,
  user: User,
  'building-2': Building2,
  'list-todo': ListTodo,
  'dollar-sign': DollarSign,
  sparkles: Sparkles,
  'map-pin': MapPin,
  network: Network,
}

function ScopeIcon({ icon, className }: { icon: string; className?: string }) {
  const Icon = ICON_MAP[icon] || MapPin
  return <Icon className={className} />
}

export function ChatScopeSelector({ options, selectedScope, onSelect, onClose }: Props) {
  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Change Chat Scope</h3>
        <button
          onClick={onClose}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
        >
          Cancel
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Choose what context the AI assistant will use for this conversation. Changing scope starts a new session.
      </p>

      <div className="space-y-1.5">
        {options.map((option) => {
          const isSelected = option.scope === selectedScope

          return (
            <button
              key={`${option.scope}-${option.entityId ?? ''}`}
              onClick={() => onSelect(option)}
              className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg transition-all text-left group ${
                isSelected
                  ? 'border-2 border-primary-500 bg-primary-50/50'
                  : 'border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected
                    ? 'bg-primary-100'
                    : 'bg-gray-100 group-hover:bg-primary-100'
                }`}
              >
                <ScopeIcon
                  icon={option.icon}
                  className={`w-[18px] h-[18px] transition-colors ${
                    isSelected
                      ? 'text-primary-600'
                      : 'text-gray-500 group-hover:text-primary-600'
                  }`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                  {option.label}
                  {option.isCurrentPage && (
                    <span className="inline-flex items-center px-1.5 py-0 bg-primary-100 text-primary-600 rounded text-[10px] font-medium">
                      Current page
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-400">{option.description}</div>
              </div>

              <div
                className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                  isSelected ? 'border-primary-500' : 'border-gray-300'
                }`}
              >
                {isSelected && (
                  <div className="w-2 h-2 rounded-full bg-primary-500" />
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
